import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProgressIndicator from '@/components/service-selection/ProgressIndicator';
import StageSelector from '@/components/service-selection/StageSelector';
import DomainSelector from '@/components/service-selection/DomainSelector';
import SubcategorySelector from '@/components/service-selection/SubcategorySelector';
import BackButton from '@/components/service-selection/BackButton';

const STORAGE_KEY = 'service_selection';

interface SelectionState {
  stage: number | null;
  domain: string | null;
  subcategory: string | null;
  step: number;
}

const ServiceSelection = () => {
  const { t } = useTranslation('service-selection');
  const navigate = useNavigate();

  const [selectionState, setSelectionState] = useState<SelectionState>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { stage: null, domain: null, subcategory: null, step: 1 };
      }
    }
    return { stage: null, domain: null, subcategory: null, step: 1 };
  });

  // Save to sessionStorage whenever state changes
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectionState));
  }, [selectionState]);

  const handleStageSelect = (stageNumber: number) => {
    setSelectionState({
      ...selectionState,
      stage: stageNumber,
      step: 2
    });
  };

  const handleDomainSelect = (domainKey: string) => {
    setSelectionState({
      ...selectionState,
      domain: domainKey,
      step: 3
    });
  };

  const handleDomainSkip = () => {
    setSelectionState({
      ...selectionState,
      domain: 'all_domains',
      step: 3
    });
  };

  const handleSubcategorySelect = (subcategoryKey: string) => {
    // Clear selection state
    sessionStorage.removeItem(STORAGE_KEY);
    
    // Save filters to sessionStorage for reliable state transfer
    sessionStorage.setItem('booking_filters', JSON.stringify({
      healingStage: selectionState.stage,
      effectiveDomain: selectionState.domain === 'all_domains' ? 'all_domains' : (selectionState.domain || 'all_domains'),
      subcategory: subcategoryKey
    }));
    
    // Navigate to book-an-appointment with filters
    navigate('/book-an-appointment', {
      state: {
        healingStage: selectionState.stage,
        effectiveDomain: selectionState.domain === 'all_domains' ? 'all_domains' : (selectionState.domain || 'all_domains'),
        subcategory: subcategoryKey
      }
    });
  };

  const handleSubcategorySkip = () => {
    // Clear selection state
    sessionStorage.removeItem(STORAGE_KEY);
    
    // Save filters to sessionStorage for reliable state transfer
    sessionStorage.setItem('booking_filters', JSON.stringify({
      healingStage: selectionState.stage,
      effectiveDomain: selectionState.domain === 'all_domains' ? 'all_domains' : (selectionState.domain || 'all_domains'),
      subcategory: 'all_subcategories'
    }));
    
    // Navigate to book-an-appointment with current filters
    navigate('/book-an-appointment', {
      state: {
        healingStage: selectionState.stage,
        effectiveDomain: selectionState.domain === 'all_domains' ? 'all_domains' : (selectionState.domain || 'all_domains'),
        subcategory: 'all_subcategories'
      }
    });
  };

  const handleBack = () => {
    if (selectionState.step === 2) {
      setSelectionState({
        ...selectionState,
        stage: null,
        domain: 'all_domains',
        subcategory: 'all_subcategories',
        step: 1
      });
    } else if (selectionState.step === 3) {
      setSelectionState({
        ...selectionState,
        domain: 'all_domains',
        subcategory: 'all_subcategories',
        step: 2
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h1 className="text-3xl md:text-4xl font-serif text-center text-foreground mb-2">
            {t('pageTitle')}
          </h1>
          
          <p className="text-center text-muted-foreground mb-12">
            {t(`stepTitles.${
              selectionState.step === 1 ? 'stage' : 
              selectionState.step === 2 ? 'domain' : 
              'subcategory'
            }`)}
          </p>

          <ProgressIndicator currentStep={selectionState.step} />

          <div className="max-w-4xl mx-auto">
            {selectionState.step === 1 && (
              <StageSelector onSelect={handleStageSelect} />
            )}

            {selectionState.step === 2 && (
              <div className="space-y-6">
                <DomainSelector 
                  onSelect={handleDomainSelect}
                  onSkip={handleDomainSkip}
                />
                <div className="flex justify-center">
                  <BackButton onClick={handleBack} />
                </div>
              </div>
            )}

            {selectionState.step === 3 && (
              <div className="space-y-6">
                <SubcategorySelector
                  domainKey={selectionState.domain}
                  onSelect={handleSubcategorySelect}
                  onSkip={handleSubcategorySkip}
                />
                <div className="flex justify-center">
                  <BackButton onClick={handleBack} />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ServiceSelection;
